import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { AttachmentPurpose, BlobInsert, BlobRow, TextractExpenseData } from '../types/supabase'
import type { DeepPartialNullable } from '../types/util'
import { PURGE } from 'redux-persist';
import { replaceOrInsert } from '../lib/util/replaceOrInsert'
import { redactObjectExceptKeys } from '../lib/util/redact'
import { present } from '../lib/util/present'
import { onSyncDownComplete } from './actions/onSyncDownComplete';

export type BlobModel = {
  id: string
  key: string
  membership_id: string
  byte_size: number
  content_type: string
  file_name: string
  file_full_path: string
  sha1: string
  /**
   * Set only by PUT /backend/memberships/{id}/blobs/{key}/content, never by the client.
   * Null means the row exists but the bytes have not been uploaded from this or another
   * device yet -- render a pending state, not a broken image.
   */
  bytes_uploaded_at?: string | null
  created_at: string
  created_by_user_id?: string | null
  updated_at: string
  deleted_at?: string | null
  is_receipt_library_eligible?: boolean
  blob_original_purpose?: AttachmentPurpose | null
  notes?: string | null
  message_id?: string | null
  textract_job_id?: string | null
  textract_job_status?: string | null
  textract_job_started_at?: string | null
  textract_job_completed_at?: string | null
  textract_job_results?: any | null
  extracted_expense?: TextractExpenseData | null
  cpt_codes: string[]
}

type BlobInsertPayload = BlobModel

export type BlobsSliceState = {
  blobs: Array<BlobModel>

  lastLoadFromServer?: number
}

const initialState: BlobsSliceState = {
  blobs: []
}

export const blobsSlice = createSlice({
  name: 'blobs',
  initialState,
  reducers: {
    /**
     * Blobs are created by the createBlob function in apps/web/src/lib/supabase/createBlob.ts.
     * this function uploads blobs and the associated files, therefore it's not appropriate as
     * a single redux action.  Instead we dispatch this to notify redux that a blob was created. 
     */
    onBlobCreated(state, action: PayloadAction<BlobModel>) {
      if (!state.blobs) { state.blobs = [] }
      const i = state.blobs.findIndex((b) => b.id === action.payload.id)
      if (i >= 0) {
        state.blobs[i] = {...action.payload}
      } else {
        state.blobs.push({...action.payload})
      }
    },
    updateBlob(state, action: PayloadAction<Partial<BlobModel> & { id: string, updated_at: string }>) {
      if (!state.blobs) { state.blobs = [] }
      const i = state.blobs.findIndex((b) => b.id === action.payload.id)
      if (i >= 0) {
        state.blobs[i] = {
          ...state.blobs[i],
          ...action.payload
        }
      }
    },
    deleteBlob(state, action: PayloadAction<{ id: string, updated_at: string, deleted_at: string }>) {
      // Remove it out of the blobs array
      if (!state.blobs) { state.blobs = [] }
      const i = state.blobs.findIndex((b) => b.id === action.payload.id)
      if (i >= 0) {
        state.blobs.splice(i, 1)
      }
    },
    /**
     * The content PUT succeeded on this device.
     *
     * Not an `updateBlob`: that queues a sync write, and `bytes_uploaded_at`
     * is not a column the client may set. This only stops the row reading as
     * still uploading while realtime or the next sync-down brings the
     * server's own timestamp.
     */
    markBlobBytesUploaded(state, action: PayloadAction<{ id: string, bytes_uploaded_at: string }>) {
      const blob = state.blobs?.find((b) => b.id === action.payload.id)
      if (!blob || present(blob.bytes_uploaded_at)) { return }
      blob.bytes_uploaded_at = action.payload.bytes_uploaded_at
    },
  },
  extraReducers: (builder) => {
    builder = builder.addCase(PURGE, (state) => {
      return initialState
    }).addCase(onSyncDownComplete, (state, action) => {
      for (const blob of action.payload.blobs?.updated || []) {
        applyDownloadedBlob(state.blobs, blob)
      }
      for (const deletedBlob of action.payload.blobs?.deleted || []) {
        const i = state.blobs.findIndex((b) => b.id === deletedBlob.id)
        if (i >= 0 && state.blobs[i].updated_at <= deletedBlob.updated_at) {
          state.blobs.splice(i, 1)
        }
      }
    })
  },
})

// Action creators are generated for each case reducer function
export const {
  onBlobCreated,
  updateBlob,
  deleteBlob,
  markBlobBytesUploaded,
} = blobsSlice.actions

/**
 * Merge one downloaded blob.
 *
 * `replaceOrInsert` drops a row whose `updated_at` sorts behind the local
 * one. That comparison is wrong for `bytes_uploaded_at`: the content
 * endpoint writes it through Eloquent, at whole seconds, while the client
 * stamped milliseconds at capture. `'…00.500Z' > '…00.000Z'` in the same
 * second, so the row that says the bytes landed is thrown away and the
 * attachment stays on "Uploading…".
 *
 * The local edit stays when it is newer. Only the server-owned upload
 * timestamp is copied across. An incoming row that still has the field
 * empty must not clear a value this device already learned — the content
 * PUT deletes the on-device bytes, and a later snapshot (a notes edit, a
 * textract write) can carry a newer `updated_at` with the timestamp still
 * empty. Losing the mark then leaves "Uploading…" and nothing to retry.
 */
function applyDownloadedBlob(blobs: BlobModel[], incoming: BlobModel) {
  const i = blobs.findIndex((b) => b.id === incoming.id)
  if (i >= 0 && blobs[i].updated_at > incoming.updated_at) {
    if (!present(blobs[i].bytes_uploaded_at) && present(incoming.bytes_uploaded_at)) {
      blobs[i].bytes_uploaded_at = incoming.bytes_uploaded_at
    }
    return
  }

  const keepUploadedAt = i >= 0
    && present(blobs[i].bytes_uploaded_at)
    && !present(incoming.bytes_uploaded_at)
    ? blobs[i].bytes_uploaded_at
    : undefined

  replaceOrInsert(blobs, incoming)

  if (keepUploadedAt) {
    const next = blobs.find((b) => b.id === incoming.id)
    if (next && !present(next.bytes_uploaded_at)) {
      next.bytes_uploaded_at = keepUploadedAt
    }
  }
}

export type BlobsSliceAction = ReturnType<typeof blobsSlice.actions[keyof typeof blobsSlice.actions]>

export function isBlobsSliceAction(action: any): action is BlobsSliceAction {
  return action.type?.startsWith(blobsSlice.name)
}

export default blobsSlice.reducer

/*
 * Redact all sensitive information from the blobs, so that they can be sent to Analytics tools like
 * Sentry or Amplitude.
 * Due to HIPAA compliance, we cannot send any patient information to these tools.
 * The redaction operates with a whitelist to ensure that we don't accidentally send any sensitive information in the future.
*/

export function redactBlobs(state: BlobsSliceState): DeepPartialNullable<BlobsSliceState> {
  return {
    blobs: state.blobs?.map(redactBlob)
  }
}

export function redactBlob(blob: Partial<BlobRow> | Partial<BlobModel>): DeepPartialNullable<BlobRow> {
  return {
    ...redactObjectExceptKeys(blob,
      'key', 'id', 'created_at', 'updated_at', 'created_by_user_id', 'membership_id',
      'byte_size', 'content_type', 'deleted_at', 'is_receipt_library_eligible',
      'blob_original_purpose', 'textract_job_id', 'textract_job_status',
      'textract_job_started_at', 'textract_job_completed_at', 'bytes_uploaded_at'),
  }
}

export function redactBlobsSliceAction(action: BlobsSliceAction) {
  switch(action.type) {
    case onBlobCreated.type:
    case updateBlob.type:
    case deleteBlob.type:
    case markBlobBytesUploaded.type:
      return {
        type: action.type,
        payload: redactBlob(action.payload)
      }

    default:
      return {
        type: (action as any).type,
        payload: 'REDACTED'
      }
  }
}

// Assert we can assign a BlobRow to a BlobModel
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _assertBlobRowAssignable: BlobModel = {} as BlobRow

// Assert we can assign a BlobModel to a BlobInsert
// Note: this may fail due to optional field differences, which is acceptable
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _assertBlobModelToBlobInsert: BlobInsert = {} as BlobModel
